import { useDispatch } from 'react-redux';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import * as yup from 'yup';
import { Typography } from '@components/Typography';
import { Input } from '@components/Input';
import { alertActions, accountActions } from '@actions';
import { ifoodIntegrationService } from '@services';
import { AccIntegrationName } from '@constants/integrations';
import { isIntegrationPending } from '@helpers/integrations';
import { ProductEvent } from '@constants/product-events';
import PostHogManager from '@widgets/posthog-manager';
import { useTranslation, useYupValidationResolver } from '@hooks';
import { handleApiError } from '@helpers/api';
import FieldWithActions, { rowActions } from '@components/FieldWithActions';
import { Modal, Content } from './styles';

const IFoodModal = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const IFoodSchema = yup.object().shape({
        cnpj: yup
            .array()
            .min(1, 'validation.required')
            .of(
                yup.object().shape({
                    id: yup.string().required('validation.required'),
                }),
            ),
    });

    const resolver = useYupValidationResolver(IFoodSchema);

    const {
        control,
        formState: { isValid, isSubmitting },
        getValues,
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            cnpj: [{ id: '' }],
        },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'cnpj',
    });

    const onConnectIFood = async () => {
        const values = getValues();
        try {
            await ifoodIntegrationService.connectIntegration({
                cnpj: values.cnpj.map(location => location.id),
            });
            const refreshed = await dispatch(accountActions.get());
            // Reconnects can go live instantly because the merchant registry survives disconnects.
            const stillPending = !refreshed || isIntegrationPending(refreshed, AccIntegrationName.iFood);
            PostHogManager.capture(ProductEvent.IntegrationConnected, { integration: 'ifood', pending: stillPending });
            dispatch(
                alertActions.success(
                    t(
                        stillPending
                            ? 'alertMessages.ifoodConnectRequestSent'
                            : 'alertMessages.connectIntegrationSuccess',
                    ),
                ),
            );
            onClose();
        } catch (error) {
            handleApiError(error, 'alertMessages.connectIntegrationFail');
        }
    };

    return (
        <Modal
            fullScreen
            isOpen={isOpen}
            handleClose={onClose}
            title={t('SettingsIntegrations.iFoodTitle')}
            subtitle={t('SettingsIntegrations.iFoodSubtitle')}
            onPrimaryAction={onConnectIFood}
            primaryActionText={t('buttons.connect')}
            primaryActionDisabled={isSubmitting || !isValid}
        >
            <Content>
                <Typography variant="caption" color="textSecondary" sx={{ marginBottom: '8px' }}>
                    {t('SettingsIntegrations.iFoodInstructions')}
                </Typography>
                <div style={{ display: 'flex', gap: '24px', flexFlow: 'column' }}>
                    {fields.map((field, index) => (
                        <FieldWithActions
                            key={field.id}
                            actions={rowActions({
                                index,
                                count: fields.length,
                                onAdd: () => append({ id: '' }),
                                onDelete: () => remove(index),
                                disabled: isSubmitting,
                                t,
                            })}
                        >
                            <Controller
                                control={control}
                                name={`cnpj.${index}.id`}
                                render={({ field, fieldState: { error }, ref }) => (
                                    <Input
                                        ref={ref}
                                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                        label={'CNPJ'}
                                        fullWidth
                                        error={!!error}
                                        helperText={error ? t(error.message) : null}
                                        required
                                    />
                                )}
                            />
                        </FieldWithActions>
                    ))}
                </div>
            </Content>
        </Modal>
    );
};

export default IFoodModal;
